import {
	forwardRef,
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import styles from "./WelcomeStep.module.less";
import classNames from "classnames";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { IconButtonWithTitle } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { useResizeObserver } from "client/Hooks";
import { ScaleToFitResult, useScaleToFit } from "client/Hooks/useScaleToFit";
import { Arrow } from "./Arrow";

export type WelcomeStepHandle = {
	resetScroll(): void;
};

const WelcomeStepInternal = forwardRef(
	(props: PropsWithChildren<{}>, ref: Ref<WelcomeStepHandle>): ReactElement => {
		const {
			children
		} = props;

		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		useImperativeHandle(
			ref,
			(): WelcomeStepHandle => ({
				resetScroll: (): void => {
					containerRef.current
						?.scrollTo(0 , 0);
				}
			}),
			[]
		);

		return (
			<div className={styles.root}>
				<div
					className={styles.container}
					ref={containerRef}
				>
					{children}
				</div>
			</div>
		);
	}
);
WelcomeStepInternal.displayName = "WelcomeStep";

type WelcomeStepWithSubComponents = {
	(props: PropsWithChildren<{}> & { ref?: Ref<WelcomeStepHandle>; }): ReactElement;
	displayName: string;
	Right: typeof WelcomeStepRight;
	Left: typeof WelcomeStepLeft;
	StepIndicator: typeof WelcomeStepStepIndicator;
	BackButton: typeof WelcomeStepBackButton;
};

export const WelcomeStep = WelcomeStepInternal as unknown as WelcomeStepWithSubComponents;

type WelcomeStepRightProps = {
	imageUrl: string;
	text?: string;
};

const WelcomeStepRight = (props: WelcomeStepRightProps): ReactElement => {
	const {
		imageUrl,
		text
	} = props;

	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		width: containerWidth,
		height: containerHeight
	} = useResizeObserver(containerRef);

	const scaleToFitResult: ScaleToFitResult | null = useScaleToFit(
		ref,
		containerWidth,
		containerHeight
	);

	return (
		<div className={styles.rightSide}>
			<div
				ref={containerRef}
				className={styles.imageContainer}
			>
				<div
					style={scaleToFitResult
						? {
							transform: `scale(${scaleToFitResult.scale})`
						}
						: undefined
					}
					className={classNames(
						styles.imageInner,
						{
							[styles.imageInner_hasText]: isNotNil(text)
						}
					)}
					ref={ref}
				>
					<img src={imageUrl} className={styles.image} />
					{text && (
						<>
							<div className={styles.arrow}>
								<Arrow />
							</div>
							<h3 className={styles.text}>
								{text}
							</h3>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
WelcomeStepRight.displayName = "WelcomeStep.Right";

type WelcomeStepProps = {
	trackingId: string;
	isVerticallyCentred?: boolean;
};

const WelcomeStepLeft = (props: PropsWithChildren<WelcomeStepProps>): ReactElement => {
	const {
		trackingId,
		isVerticallyCentred = false,
		children
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.leftSide}
		>
			<div
				className={classNames(
					styles.content,
					{
						[styles.content_verticallyCentred]: isVerticallyCentred
					}
				)}
			>
				{children}
			</div>
		</div>
	);
};
WelcomeStepLeft.displayName = "WelcomeStep.Left";

type WelcomeStepBackButtonProps = {
	onClick(): void;
	isHidden?: boolean;
};

const WelcomeStepBackButton = (props: WelcomeStepBackButtonProps): ReactElement => {
	const {
		isHidden = false,
		onClick
	} = props;

	return (
		<div
			className={classNames(
				styles.backButtonContainer,
				{[styles.backButtonContainer_hidden]: isHidden}
			)}
		>
			<IconButtonWithTitle
				title={localize("_Back")}
				icon="chevron-left"
				onClick={onClick}
				trackingId="page-content-back"
			/>
		</div>
	);
};
WelcomeStepBackButton.displayName = "WelcomeStep.BackButton";

export type WelcomeStepStepsInfo = {
	active: number;
	total: number;
};

type WelcomeStepStepIndicatorProps = {
	stepsInfo: WelcomeStepStepsInfo;
};

const WelcomeStepStepIndicator = (props: WelcomeStepStepIndicatorProps): ReactElement => {
	const {
		stepsInfo
	} = props;

	return (
		<ol className={styles.stepIndicator}>
			{range(stepsInfo.total).map(
				i => (
					<li
						key={i}
						className={classNames(
							styles.step,
							{
								[styles.step_active]: stepsInfo.active === i
							}
						)}
					/>
				)
			)}
		</ol>
	);
};
WelcomeStepStepIndicator.displayName = "WelcomeStep.StepIndicator";

WelcomeStep.Right = WelcomeStepRight;
WelcomeStep.Left = WelcomeStepLeft;
WelcomeStep.StepIndicator = WelcomeStepStepIndicator;
WelcomeStep.BackButton = WelcomeStepBackButton;

